/**
 * **P-086 — the web half.** Board-level changes reaching a second tab.
 *
 * The item-level patches were always fine: `item.*`, `cell.changed` and `column.*` go through
 * `lib/realtime/patches.ts` into the **table store**, which is why T1, C1 and C6 repainted live
 * from the start. The board's *chrome* does not live in that store at all — the header reads
 * `shell.board` and the tab bar reads `views.list`, two TanStack Query caches — so adding a
 * view or renaming the board was invisible in a second tab until it was reloaded.
 *
 * Round 10f measured the server half and found nothing missing: every board-level mutation
 * already writes its outbox row, the relay is type-agnostic and a socket joined to
 * `board:<id>` receives all six events (`apps/api/src/realtime/board-chrome-events.test.ts`).
 * The whole remaining fix is this listener.
 *
 * | event | what this does |
 * |---|---|
 * | `board.updated` · `board.restored` · `board.moved` | invalidate `shell.board` for the board |
 * | `view.created` · `view.updated` · `view.deleted` | invalidate `views.list` for the board |
 * | `column.created` · `column.deleted` · `column.updated` | invalidate **both**: the header's column count and any chrome drawn off the schema |
 *
 * Both caches are keyed by board id, so every case is an `invalidateQueries`, never a merge —
 * a view carries settings this client may be editing and merging two people's view settings is
 * not something F5 promises (§5.2).
 *
 * It is mounted by the board **screen**, beside the header and the tab bar, rather than inside
 * `TableViewProvider`: the chrome is the same on a kanban or a gallery tab, and the provider is
 * a table-view concern. It only *listens* — the room is joined by whichever view mounted
 * `useBoardRealtime`, so on a view kind that joins no room this is inert, exactly as it was.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from '../../lib/trpc';
import { onEvent } from '../../lib/realtime/socket';

/** The events that change the board header. */
const BOARD_EVENTS = new Set(['board.updated', 'board.restored', 'board.moved']);
/** The events that change the view tabs. */
const VIEW_EVENTS = new Set(['view.created', 'view.updated', 'view.deleted', 'view.restored']);
/** A column changes the board's schema, which the header's counters and pickers read. */
const COLUMN_EVENTS = new Set(['column.created', 'column.deleted', 'column.updated']);

export interface ChromeInvalidation {
  /** `shell.board` — the header's name, kind, counters and description. */
  readonly board: boolean;
  /** `views.list` — the tab bar. */
  readonly views: boolean;
}

/**
 * Which of the two chrome caches an event type invalidates. Pure, so the mapping is a unit
 * test rather than a socket integration test; the hook below is the three lines that call it.
 */
export function chromeInvalidationsFor(type: string): ChromeInvalidation {
  const column = COLUMN_EVENTS.has(type);
  return { board: column || BOARD_EVENTS.has(type), views: column || VIEW_EVENTS.has(type) };
}

export function useBoardChromeRealtime(boardId: string | null): void {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!boardId) return;
    /*
     * The keys are read off the **same** `queryOptions` the two hooks build
     * (`useBoardSummary`, `useBoardViews`), not off the `.queryKey()` shorthand: that is the
     * only spelling that cannot drift from what those hooks actually registered.
     */
    const boardKey = trpc.shell.board.queryOptions({ boardId }).queryKey;
    const viewsKey = trpc.views.list.queryOptions({ boardId }).queryKey;

    return onEvent((event) => {
      // Another board's room on the same socket is not ours to invalidate.
      if (event.boardId && String(event.boardId) !== boardId) return;
      const { board, views } = chromeInvalidationsFor(String(event.type));
      if (board) void queryClient.invalidateQueries({ queryKey: boardKey });
      if (views) void queryClient.invalidateQueries({ queryKey: viewsKey });
    });
  }, [boardId, trpc, queryClient]);
}
